import React, { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import BookIcon from '@mui/icons-material/Book';
import AssignmentIcon from '@mui/icons-material/Assignment';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from '../context/AuthContext';
import { AccountTree } from '@mui/icons-material';

const DRAWER_WIDTH = 250;

const AdminLayout = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin/dashboard' },
    { text: 'Students', icon: <PersonIcon />, path: '/admin/students' },
    { text: 'Courses', icon: <SchoolIcon />, path: '/admin/courses' },
    { text: 'Subjects', icon: <BookIcon />, path: '/admin/subjects' },
    { text: 'Branches', icon: <AccountTree />, path: '/admin/branches' },
    { text: 'Results', icon: <AssignmentIcon />, path: '/admin/results' },
    { text: 'Student Results', icon: <AssignmentIcon />, path: '/admin/student-results' },
      { text: 'Admissions', icon: <SchoolIcon />, path: '/admin/admissions' },
      { text: 'Products', icon: <SchoolIcon />, path: '/admin/products' },
  ];

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: { xs: 1.5, sm: 2 }, backgroundColor: '#1976d2', color: 'white' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Kavya Institute
        </Typography>
        <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
          Admin Portal
        </Typography>
      </Box>

      <Divider />

      {/* User Info */}
      <Box sx={{ p: { xs: 1.5, sm: 2 }, backgroundColor: '#f5f5f5' }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
          {user?.name || 'Admin User'}
        </Typography>
        <Typography variant="caption" sx={{ color: '#666', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
          {user?.email}
        </Typography>
      </Box>

      <Divider />

      {/* Menu Items */}
      <List sx={{ flexGrow: 1, pt: { xs: 1, sm: 2 } }}>
        {menuItems.map((item) => (
          <ListItem
            key={item.text}
            disablePadding
            onClick={() => {
              navigate(item.path);
              setMobileOpen(false);
            }}
            sx={{
              '&:hover': {
                backgroundColor: '#e3f2fd',
              },
            }}
          >
            <ListItemButton>
              <ListItemIcon sx={{ color: '#1976d2', minWidth: { xs: 40, sm: 56 } }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text}
                primaryTypographyProps={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider />

      {/* Logout */}
      <ListItem
        disablePadding
        onClick={handleLogout}
        sx={{
          backgroundColor: '#ffebee',
          '&:hover': {
            backgroundColor: '#ffcdd2',
          },
        }}
      >
        <ListItemButton>
          <ListItemIcon sx={{ color: '#f44336', minWidth: { xs: 40, sm: 56 } }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText 
            primary="Logout"
            primaryTypographyProps={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}
          />
        </ListItemButton>
      </ListItem>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Drawer for Desktop (md and above) */}
      <Drawer
        variant="permanent"
        sx={{
          width: { md: DRAWER_WIDTH },
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            boxShadow: 2,
          },
          display: { xs: 'none', md: 'block' },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Drawer for Mobile & Tablet (below md) */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
          },
          display: { xs: 'block', md: 'none' },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', width: '100%' }}>
        {/* Top AppBar with Menu */}
        <AppBar
          position="sticky"
          sx={{
            backgroundColor: '#1976d2',
            boxShadow: 2,
            display: { xs: 'flex', md: 'none' },
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <IconButton
              color="inherit"
              onClick={() => setMobileOpen(!mobileOpen)}
              sx={{ display: { xs: 'block', md: 'none' } }}
            >
              {mobileOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Kavya Portal
            </Typography>
            <Box sx={{ width: 40 }} />
          </Toolbar>
        </AppBar>

        {/* Page Content */}
        <Box
          sx={{
            flexGrow: 1,
            p: { xs: 1.5, sm: 2, md: 3, lg: 4 },
            overflow: 'auto',
            maxWidth: '100%',
          }}
        >
          <Outlet />
        </Box>

        {/* Footer */}
        <Box sx={{ p: 2, backgroundColor: '#fff', borderTop: '1px solid #e0e0e0', textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: '#666' }}>
            &copy; 2025 Kavya Institute. All rights reserved.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayout;
